import React, { useCallback, useState, useEffect, useRef } from 'react';
import { RangeSliderWithHistogramProps, DistributionBucket } from './types';
import InfoTooltip from '../../common/InfoTooltip';

const HISTOGRAM_HEIGHT = 32;

/**
 * Dual-thumb range slider with an optional distribution histogram.
 * Drags update locally for instant feedback and commit on release; keyboard
 * changes commit right away, and a release outside the track still commits.
 */
const RangeSliderWithHistogram: React.FC<RangeSliderWithHistogramProps> = ({
  label,
  min,
  max,
  step = 1,
  value,
  onChange,
  distribution,
  formatValue,
  highlightRange,
  unit = '',
  tooltip,
}) => {
  const [localValue, setLocalValue] = useState<[number, number]>(value);
  const [isDragging, setIsDragging] = useState(false);
  const localRef = useRef(localValue);
  localRef.current = localValue;

  useEffect(() => {
    if (!isDragging) setLocalValue(value);
  }, [value, isDragging]);

  const commit = useCallback(
    (next: [number, number]) => {
      if (next[0] !== value[0] || next[1] !== value[1]) onChange(next);
    },
    [value, onChange]
  );

  // A drag can end anywhere on the page: listen on the window while dragging.
  useEffect(() => {
    if (!isDragging) return;
    const end = () => {
      setIsDragging(false);
      commit(localRef.current);
    };
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
    return () => {
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
    };
  }, [isDragging, commit]);

  const hasHistogram = !!distribution && distribution.some((d) => d.count > 0);
  const maxCount = hasHistogram ? Math.max(...distribution!.map((d) => d.count), 1) : 1;

  const pct = useCallback((val: number) => ((val - min) / (max - min)) * 100, [min, max]);

  const inRange = (b: DistributionBucket) => (b.max ?? max) > localValue[0] && b.min < localValue[1];

  const fmt = (val: number) => (formatValue ? formatValue(val) : val.toString());
  const isFull = localValue[0] <= min && localValue[1] >= max;

  const handle = (which: 'min' | 'max') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const n = Number(e.target.value);
    const next: [number, number] | null =
      which === 'min' ? (n < localValue[1] ? [n, localValue[1]] : null) : n > localValue[0] ? [localValue[0], n] : null;
    if (!next) return;
    setLocalValue(next);
    if (!isDragging) commit(next); // keyboard or programmatic change
  };

  return (
    <div className="fs-field fs-range">
      <div className="fs-field-head">
        <span className="fs-label">
          {label}
          {tooltip && <InfoTooltip text={tooltip} />}
        </span>
        <span className={`fs-readout ${isFull ? 'is-any' : ''}`}>
          {isFull ? 'Any' : `${fmt(localValue[0])}${unit} – ${fmt(localValue[1])}${unit}`}
        </span>
      </div>

      {hasHistogram && (
        <svg
          className="fs-hist"
          width="100%"
          height={HISTOGRAM_HEIGHT}
          viewBox={`0 0 100 ${HISTOGRAM_HEIGHT}`}
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {highlightRange && (
            <rect
              x={pct(highlightRange[0])}
              y={0}
              width={pct(highlightRange[1]) - pct(highlightRange[0])}
              height={HISTOGRAM_HEIGHT}
              className="fs-hist-zone"
            />
          )}
          {distribution!.map((bucket, i) => {
            const slot = 100 / distribution!.length;
            const h = (bucket.count / maxCount) * (HISTOGRAM_HEIGHT - 2);
            return (
              <rect
                key={i}
                x={i * slot + 0.15}
                y={HISTOGRAM_HEIGHT - h}
                width={Math.max(slot - 0.3, 0.2)}
                height={h}
                className={inRange(bucket) ? 'fs-bar is-in' : 'fs-bar'}
              />
            );
          })}
        </svg>
      )}

      <div className="fs-track-wrap">
        <div className="fs-track" />
        <div
          className="fs-track-fill"
          style={{ left: `${pct(localValue[0])}%`, width: `${pct(localValue[1]) - pct(localValue[0])}%` }}
        />
        <input
          type="range"
          className="fs-thumb"
          min={min}
          max={max}
          step={step}
          value={localValue[0]}
          aria-label={`${label} minimum`}
          aria-valuetext={`${fmt(localValue[0])}${unit}`}
          onChange={handle('min')}
          onPointerDown={() => setIsDragging(true)}
          style={{ zIndex: localValue[0] > max - (max - min) * 0.1 ? 5 : 3 }}
        />
        <input
          type="range"
          className="fs-thumb"
          min={min}
          max={max}
          step={step}
          value={localValue[1]}
          aria-label={`${label} maximum`}
          aria-valuetext={`${fmt(localValue[1])}${unit}`}
          onChange={handle('max')}
          onPointerDown={() => setIsDragging(true)}
          style={{ zIndex: 4 }}
        />
      </div>
      <div className="fs-bounds" aria-hidden="true">
        <span>{fmt(min)}{unit}</span>
        <span>{fmt(max)}{unit}</span>
      </div>
    </div>
  );
};

export default RangeSliderWithHistogram;
